/**
 * MaintenanceGuard
 *
 * Higher-Order Component (HOC) que verifica se uma rota está em manutenção.
 * Se a rota estiver bloqueada, redireciona para MaintenanceBlockedPage.
 * Superusuários têm acesso mesmo durante manutenção.
 *
 * @component
 * @example
 * <MaintenanceGuard route="/chat-interface">
 *   <ChatInterface />
 * </MaintenanceGuard>
 */

import React, { useState, useEffect } from 'react';
import { isRouteInMaintenance } from '../utils/admin/maintenanceService';
import { checkIsSuperuser } from '../utils/admin/adminService';
import { useAuth } from '../utils/AuthContext';
import MaintenanceBlockedPage from '../pages/MaintenanceBlockedPage';
import { logger } from '../utils/logger';

// Cache para reduzir chamadas ao banco
const maintenanceCache = new Map();
const CACHE_DURATION = 5000; // 5 segundos (reduzido para melhor responsividade)

/**
 * Limpa o cache de manutenção globalmente
 * Chamado após alterações no painel de manutenção
 */
export const clearMaintenanceCache = () => {
  maintenanceCache.clear();
  logger.log('🗑️ [MaintenanceGuard] Cache global limpo');
};

const MaintenanceGuard = ({ children, route = '/' }) => {
  const { user, loading: authLoading } = useAuth();
  const [isBlocked, setIsBlocked] = useState(false);
  const [isSuperuser, setIsSuperuser] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkMaintenance = async () => {
      // Aguarda autenticação carregar
      if (authLoading) {
        return;
      }

      try {
        // 1. Verificar se usuário é superuser (se autenticado)
        let superuser = false;
        if (user?.id) {
          try {
            superuser = await checkIsSuperuser(user.id);
            setIsSuperuser(superuser);
            logger.log(`🔐 [MaintenanceGuard] Superuser: ${superuser}`);
          } catch (error) {
            logger.warn('⚠️ [MaintenanceGuard] Erro ao verificar superuser:', error);
          }
        }

        // Se for superuser, sempre permite acesso
        if (superuser) {
          setIsBlocked(false);
          setLoading(false);
          return;
        }

        // 2. Verificar cache primeiro
        const cacheKey = `maintenance_${route}`;
        const cached = maintenanceCache.get(cacheKey);
        const now = Date.now();

        if (cached && (now - cached.timestamp) < CACHE_DURATION) {
          logger.log(`⚡ [MaintenanceGuard] Usando cache para ${route}:`, cached.value);
          setIsBlocked(cached.value);
          setLoading(false);
          return;
        }

        // 3. Verificar se rota está em manutenção
        const blocked = await isRouteInMaintenance(route);

        // Atualizar cache
        maintenanceCache.set(cacheKey, {
          value: blocked,
          timestamp: now,
        });

        logger.log(`🔧 [MaintenanceGuard] Rota ${route} bloqueada:`, blocked);
        setIsBlocked(blocked);
      } catch (error) {
        logger.error('❌ [MaintenanceGuard] Erro ao verificar manutenção:', error);
        // Em caso de erro, permite acesso (fail-safe)
        setIsBlocked(false);
      } finally {
        setLoading(false);
      }
    };

    checkMaintenance();

    // Cleanup: invalidar cache quando componente desmonta
    return () => {
      // Não limpa o cache aqui para aproveitar entre navegações
    };
  }, [route, user, authLoading]);

  // Loading state
  if (loading || authLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="text-muted-foreground font-body">Carregando...</p>
        </div>
      </div>
    );
  }

  // Se bloqueada e não é superuser, mostra página de manutenção
  if (isBlocked && !isSuperuser) {
    return <MaintenanceBlockedPage route={route} />;
  }

  // Permite acesso
  return children;
};

/**
 * Hook para invalidar o cache de manutenção
 * Útil quando o admin faz alterações na configuração
 */
export const useInvalidateMaintenanceCache = () => {
  return () => {
    maintenanceCache.clear();
    logger.log('🗑️ [MaintenanceGuard] Cache limpo');
  };
};

export default MaintenanceGuard;
